import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  CreateInfirmaryVisitData,
  InfirmaryVisit,
  InfirmaryVisitListParams,
  UpdateInfirmaryVisitData,
  VisitAttachmentTag,
  medicalVisitsApi,
} from '../infirmary-visits';

import { medicalHubKeys } from './use-medical-hub';

export const infirmaryVisitKeys = {
  all: ['medical', 'infirmary-visits'] as const,
  byDateRange: (from: string, to: string, params?: InfirmaryVisitListParams) =>
    [...infirmaryVisitKeys.all, 'range', from, to, params ?? {}] as const,
  forStudent: (studentId: string) => [...infirmaryVisitKeys.all, 'student', studentId] as const,
  detail: (id: string) => [...infirmaryVisitKeys.all, 'detail', id] as const,
};

export function useInfirmaryVisitsByDateRangeQuery(from: string, to: string, params?: InfirmaryVisitListParams) {
  return useQuery({
    queryKey: infirmaryVisitKeys.byDateRange(from, to, params),
    queryFn: () => medicalVisitsApi.listByDateRange(from, to, params),
    enabled: Boolean(from),
  });
}

export function useInfirmaryVisitQuery(id?: string) {
  return useQuery({
    queryKey: infirmaryVisitKeys.detail(id ?? ''),
    queryFn: () => medicalVisitsApi.getById(id as string),
    enabled: Boolean(id),
  });
}

export function useStudentInfirmaryVisitsQuery(studentId: string) {
  return useQuery({
    queryKey: infirmaryVisitKeys.forStudent(studentId),
    queryFn: () => medicalVisitsApi.listForStudent(studentId),
    enabled: Boolean(studentId),
  });
}

export function useCreateInfirmaryVisitMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateInfirmaryVisitData) => medicalVisitsApi.create(data),
    onSuccess: visit => {
      qc.invalidateQueries({ queryKey: [...infirmaryVisitKeys.all, 'range'] });
      qc.invalidateQueries({ queryKey: infirmaryVisitKeys.forStudent(visit.studentId) });
      qc.invalidateQueries({ queryKey: medicalHubKeys.todayAll });
    },
  });
}

export function useUpdateInfirmaryVisitMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateInfirmaryVisitData }) => medicalVisitsApi.update(id, data),
    onSuccess: visit => {
      qc.invalidateQueries({ queryKey: [...infirmaryVisitKeys.all, 'range'] });
      // Every student list, not just the new student's: the visit may have moved off another child.
      qc.invalidateQueries({ queryKey: [...infirmaryVisitKeys.all, 'student'] });
      qc.invalidateQueries({ queryKey: infirmaryVisitKeys.detail(visit.id) });
      qc.invalidateQueries({ queryKey: medicalHubKeys.todayAll });
    },
  });
}

export function useDeleteInfirmaryVisitMutation(studentId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => medicalVisitsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...infirmaryVisitKeys.all, 'range'] });
      if (studentId) {
        qc.invalidateQueries({ queryKey: infirmaryVisitKeys.forStudent(studentId) });
      }
      qc.invalidateQueries({ queryKey: medicalHubKeys.todayAll });
    },
  });
}

export function useUploadVisitPhotosMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ visitId, files, tag }: { visitId: string; files: File[]; tag: VisitAttachmentTag }) =>
      medicalVisitsApi.uploadPhotos(visitId, files, tag),
    onSuccess: visit => {
      qc.invalidateQueries({ queryKey: [...infirmaryVisitKeys.all, 'range'] });
      // Every student list, not just the new student's: the visit may have moved off another child.
      qc.invalidateQueries({ queryKey: [...infirmaryVisitKeys.all, 'student'] });
      qc.invalidateQueries({ queryKey: infirmaryVisitKeys.detail(visit.id) });
    },
  });
}

/** The three places a changed visit shows up: the daily log, the student's history, the detail. */
function invalidateVisit(qc: ReturnType<typeof useQueryClient>, visit: InfirmaryVisit): void {
  qc.invalidateQueries({ queryKey: [...infirmaryVisitKeys.all, 'range'] });
  qc.invalidateQueries({ queryKey: infirmaryVisitKeys.forStudent(visit.studentId) });
  qc.invalidateQueries({ queryKey: infirmaryVisitKeys.detail(visit.id) });
}

export function useShareVisitWithFamilyMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      familyNote,
      attachmentIds,
    }: {
      id: string;
      familyNote?: string | null;
      attachmentIds: string[];
    }) => medicalVisitsApi.share(id, { familyNote, attachmentIds }),
    onSuccess: visit => invalidateVisit(qc, visit),
  });
}

export function useUnshareVisitFromFamilyMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => medicalVisitsApi.unshare(id),
    onSuccess: visit => invalidateVisit(qc, visit),
  });
}

export function useUpdateVisitAttachmentTagMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ attId, tag }: { attId: string; tag: VisitAttachmentTag }) =>
      medicalVisitsApi.updateAttachmentTag(attId, tag),
    onSuccess: visit => {
      qc.invalidateQueries({ queryKey: [...infirmaryVisitKeys.all, 'range'] });
      // Every student list, not just the new student's: the visit may have moved off another child.
      qc.invalidateQueries({ queryKey: [...infirmaryVisitKeys.all, 'student'] });
      qc.invalidateQueries({ queryKey: infirmaryVisitKeys.detail(visit.id) });
    },
  });
}

/** `visitId` is what lets the detail page's gallery refresh; the daily log has no use for it. */
export function useDeleteVisitAttachmentMutation(studentId?: string, visitId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attId: string) => medicalVisitsApi.deleteAttachment(attId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...infirmaryVisitKeys.all, 'range'] });
      if (studentId) qc.invalidateQueries({ queryKey: infirmaryVisitKeys.forStudent(studentId) });
      if (visitId) qc.invalidateQueries({ queryKey: infirmaryVisitKeys.detail(visitId) });
    },
  });
}
